import React, { useEffect, useState, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { 
  Shield, 
  ShieldCheck, 
  Users, 
  Eye, 
  Bot, 
  Phone, 
  Building2, 
  ShieldX, 
  Wrench, 
  History,
  AlertCircle,
  CheckCircle2,
  Loader2,
  UserCog,
  Mail,
  Workflow,
  BarChart3,
  UsersRound,
  Book,
  CreditCard,
  Copy,
  Check,
  User,
  FileText,
  MessageSquare,
  Puzzle
} from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import type { MemberPermissions, Roles } from '@/api/users/users.types';
import { cn } from "@/lib/utils";
import { useEnhancedToast } from '@/components/ui/enhanced-toast';
import { useAuth } from "@/context/AuthContext";
import { ScrollArea } from "@/components/ui/scroll-area";

const ROLES = [
  {
    value: "company_admin",
    label: "Company Admin",
    description: "Full access to all features with analytics view",
    icon: Shield,
  },
  {
    value: "team_member",
    label: "Team Member",
    description: "Customizable access to features",
    icon: Users,
  }
] as const;

// Icons and order match the sidebar from routes.tsx for consistency
const PERMISSION_SECTIONS = [
  { id: 'analytics', label: 'Analytics', icon: BarChart3 },
  { id: 'rubrics', label: 'Rubrics', icon: FileText },
  { id: 'workflows', label: 'Workflows', icon: Workflow },
  { id: 'assistant', label: 'Assistants', icon: Bot },
  { id: 'phone_numbers', label: 'Phone Numbers', icon: Phone },
  { id: 'tools', label: 'Tools', icon: Wrench },
  { id: 'files', label: 'Files', icon: FileText },
  { id: 'knowledge_base', label: 'Knowledge Base', icon: Book },
  { id: 'multi_agent', label: 'Multi Agent', icon: UsersRound },
  { id: 'messaging', label: 'Messaging', icon: MessageSquare },
  { id: 'widgets', label: 'Widgets', icon: Puzzle },
  { id: 'calls', label: 'Call Logs', icon: History },
  { id: 'team_management', label: 'Team Management', icon: Building2 },
  { id: 'billing', label: 'Billing', icon: CreditCard }
] as const;

type Permission = "EDITOR" | "VIEWER" | "RESTRICTED";

const PERMISSION_LEVELS: Record<Permission, { 
  label: string; 
  description: string;
  icon: LucideIcon;
  color: string;
}> = {
  EDITOR: { 
    label: "Editor", 
    description: "Can edit and manage content",
    icon: Users,
    color: "text-blue-500"
  },
  VIEWER: { 
    label: "Viewer", 
    description: "Can only view content",
    icon: Eye,
    color: "text-gray-500"
  },
  RESTRICTED: { 
    label: "Restricted", 
    description: "No access to this feature",
    icon: ShieldX,
    color: "text-destructive"
  }
};

const defaultPermissions: MemberPermissions = {
  workflows: "RESTRICTED",
  assistant: "RESTRICTED",
  multi_agent: "RESTRICTED",
  knowledge_base: "RESTRICTED",
  analytics: "RESTRICTED",
  billing: "RESTRICTED",
  team_management: "RESTRICTED",
  phone_numbers: "RESTRICTED",
  calls: "RESTRICTED",
  tools: "RESTRICTED",
  files: "RESTRICTED",
  messaging: "RESTRICTED",
  rubrics: "RESTRICTED",
  widgets: "RESTRICTED"
};

interface EditRoleDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  member: {
    user_id: string;
    first_name?: string;
    last_name?: string;
    email: string;
    role: Roles;
    permissions: MemberPermissions;
  } | null;
  onSave: (role: Roles, permissions: MemberPermissions) => void;
}

export const EditRoleDialog = ({
  open,
  onOpenChange,
  member,
  onSave,
}: EditRoleDialogProps) => {
  const { user } = useAuth();
  const { error, success } = useEnhancedToast();
  const [selectedRole, setSelectedRole] = useState<Roles>("team_member");
  const [selectedPermissions, setSelectedPermissions] = useState<MemberPermissions>(defaultPermissions);
  const [isSaving, setIsSaving] = useState(false);
  const [copiedEmail, setCopiedEmail] = useState(false);

  // Create refs for toast functions
  const errorRef = useRef(error);
  const successRef = useRef(success);

  // Update refs when toast functions change
  useEffect(() => {
      errorRef.current = error;
      successRef.current = success;
  }, [error, success]);

  const isCurrentUserAdmin = user?.role === "company_admin";

  useEffect(() => {
    if (open && member) {
      setSelectedRole(member.role);
      setSelectedPermissions(member.permissions || defaultPermissions);
    } else if (!open) {
      setSelectedRole("team_member");
      setSelectedPermissions(defaultPermissions);
    }
  }, [open, member]);

  const handleSave = async () => {
    if (!member) return;
    
    // Check if all permissions are RESTRICTED
    const hasAtLeastOneViewer = Object.values(selectedPermissions).some(
      permission => permission === "VIEWER" || permission === "EDITOR"
    );

    if (!hasAtLeastOneViewer) {
      errorRef.current({
        icon: AlertCircle,
        title: "Invalid Permissions",
        description: "At least one section must have VIEWER or EDITOR access. Having all sections restricted would prevent the user from accessing any functionality.",
        duration: 4000,
      });
      return;
    }
    
    setIsSaving(true);
    try {
      await onSave(selectedRole, selectedPermissions);
      onOpenChange(false);
      successRef.current({
        icon: CheckCircle2,
        title: "Role Updated",
        description: "Member role and permissions have been updated successfully",
        duration: 4000,
      });
    } catch (err) {
      errorRef.current({
        icon: AlertCircle,
        title: "Update Failed",
        description: err instanceof Error ? err.message : "Could not update member role and permissions",
        duration: 4000,
      });
    } finally {
      setIsSaving(false);
    }
  };

  const getCompanyAdminValue = (sectionId: string) => {
    // View-only sections (no CRUD operations, only display data)
    const viewOnlySections = ["analytics", "calls", "billing"];
    if (viewOnlySections.includes(sectionId)) {
      return "VIEWER";
    }
    return "EDITOR";
  };

  const isPermissionDisabled = (role: Roles) => {
    return role === "company_admin";
  };

  const getAvailablePermissions = (role: Roles, sectionId: string): Permission[] => {
    // View-only sections (no CRUD operations, only display data)
    const viewOnlySections = ["analytics", "calls", "billing"];
    
    if (role === "company_admin") {
      if (viewOnlySections.includes(sectionId)) {
        return ["VIEWER"];
      }
      return ["EDITOR"];
    }
    // View-only sections for team members
    if (viewOnlySections.includes(sectionId)) {
      return ["VIEWER", "RESTRICTED"];
    }
    return ["EDITOR", "VIEWER", "RESTRICTED"];
  };

  if (!member) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg p-0 h-[90vh] max-h-[90vh] flex flex-col">
        <DialogHeader className="border-b bg-muted/20 px-6 py-3 shrink-0">
          <div className="flex items-center gap-3">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
              <UserCog className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 space-y-1">
              <DialogTitle className="text-base font-semibold">
                Edit Role & Permissions
              </DialogTitle>
              <DialogDescription className="text-xs text-muted-foreground">
                Update member role and access permissions
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 min-h-0 overflow-y-auto px-6 pt-4 pb-6">
          <div className="space-y-6">
            {/* Member Info & Role Card */}
            <Card className="shadow-sm">
              <CardHeader className="border-b bg-muted/20 px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                    <UserCog className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-sm">Member Details & Role</CardTitle>
                    <CardDescription className="text-xs">
                      User information and role assignment
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                {/* User Info */}
                <div className="flex items-center gap-3">
                  {(() => {
                    // Check if user has signed up (has first_name and last_name)
                    const hasSignedUp = member.first_name && member.last_name;
                    const initials = hasSignedUp 
                      ? `${member.first_name[0]}${member.last_name[0]}`.toUpperCase()
                      : null;

                    return (
                      <>
                        <Avatar className="h-10 w-10">
                          <AvatarImage src="" />
                          <AvatarFallback className={cn(
                            "text-xs font-semibold",
                            hasSignedUp 
                              ? "bg-primary/10 text-primary" 
                              : "bg-muted text-muted-foreground"
                          )}>
                            {hasSignedUp ? (
                              initials
                            ) : (
                              <User className="h-4 w-4" />
                            )}
                          </AvatarFallback>
                        </Avatar>
                        <div className="flex-1 min-w-0">
                          {hasSignedUp ? (
                            <p className="text-sm font-semibold truncate">
                              {member.first_name} {member.last_name}
                            </p>
                          ) : (
                            <p className="text-sm italic text-muted-foreground truncate">
                              Pending signup
                            </p>
                          )}
                          <div className="flex items-center gap-2">
                            <p className="text-xs text-muted-foreground truncate">{member.email}</p>
                            <Button
                              type="button"
                              variant="ghost"
                              size="icon"
                              className="h-6 w-6 shrink-0"
                              onClick={() => {
                                navigator.clipboard.writeText(member.email);
                                setCopiedEmail(true);
                                setTimeout(() => setCopiedEmail(false), 2000);
                              }}
                            >
                              {copiedEmail ? (
                                <Check className="h-3.5 w-3.5 text-green-500" />
                              ) : (
                                <Copy className="h-3.5 w-3.5" />
                              )}
                            </Button>
                          </div>
                        </div>
                      </>
                    );
                  })()}
                </div>

                {/* Role Selection */}
                <div className="space-y-2">
                  <label className="text-sm font-medium flex items-center gap-2">
                    <Shield className="h-4 w-4 text-primary" />
                    Role
                  </label>
                  <Select 
                  value={selectedRole} 
                  onValueChange={(value: Roles) => {
                    setSelectedRole(value);
                    if (value === "company_admin") {
                      const newPermissions = PERMISSION_SECTIONS.reduce((acc, section) => ({
                        ...acc,
                        [section.id]: getCompanyAdminValue(section.id)
                      }), {});
                      setSelectedPermissions(newPermissions as MemberPermissions);
                    }
                  }}
                  disabled={!isCurrentUserAdmin}
                >
                  <SelectTrigger className="h-9">
                    <SelectValue>
                      {selectedRole && (() => {
                        const selectedRoleInfo = ROLES.find(r => r.value === selectedRole);
                        if (!selectedRoleInfo) return null;
                        const Icon = selectedRoleInfo.icon;
                        return (
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 flex-shrink-0" />
                            <span className="font-medium">{selectedRoleInfo.label}</span>
                          </div>
                        );
                      })()}
                    </SelectValue>
                  </SelectTrigger>
                  <SelectContent>
                    {ROLES.filter(role => 
                      isCurrentUserAdmin ? true : role.value === "team_member"
                    ).map((role) => (
                      <SelectItem
                        key={role.value}
                        value={role.value}
                        className="py-3"
                      >
                        <div className="flex items-center gap-3">
                          {React.createElement(role.icon, { className: "h-4 w-4 flex-shrink-0" })}
                          <div className="flex flex-col gap-1">
                            <div className="font-medium leading-none">{role.label}</div>
                            <div className="text-xs text-muted-foreground leading-none mt-1">{role.description}</div>
                          </div>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                </div>
              </CardContent>
            </Card>

            {/* Permissions Card */}
            <Card className="shadow-sm flex-1 flex flex-col">
              <CardHeader className="border-b bg-muted/20 px-4 py-3">
                <div className="flex items-center gap-3">
                  <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                    <ShieldCheck className="h-4 w-4 text-primary" />
                  </div>
                  <div className="flex-1">
                    <CardTitle className="text-sm">Permissions</CardTitle>
                    <CardDescription className="text-xs">
                      Set access level for each feature
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="p-0 flex-1 overflow-hidden">
                <ScrollArea className="h-full p-4">
                  <div className="space-y-3">
                    {PERMISSION_SECTIONS.map((section) => {
                      const Icon = section.icon;
                      const permissionValue = selectedPermissions[section.id] as Permission;
                      const availablePermissions = getAvailablePermissions(selectedRole, section.id);
                      const isDisabled = isPermissionDisabled(selectedRole);
                      const fixedValue = selectedRole === "company_admin" ? getCompanyAdminValue(section.id) as Permission : undefined;

                      return (
                        <div key={section.id} className="flex items-center justify-between group">
                          <div className="flex items-center gap-2">
                            <Icon className="h-4 w-4 text-primary" />
                            <span className="text-sm font-medium">
                              {section.label}
                            </span>
                          </div>
                          {selectedRole === "company_admin" ? (
                            <div className="flex items-center gap-2 px-2 py-1 bg-primary/5 rounded-md border border-primary/10">
                              <ShieldCheck className="h-3.5 w-3.5 text-primary" />
                              <span className="text-sm font-medium text-primary">
                                {["analytics", "calls", "billing"].includes(section.id) ? "Viewer" : "Editor"}
                              </span>
                            </div>
                          ) : (
                            <Select
                              value={fixedValue || permissionValue}
                              onValueChange={(value: Permission) => {
                                setSelectedPermissions(prev => ({
                                  ...prev,
                                  [section.id]: value
                                }));
                              }}
                              disabled={isDisabled}
                            >
                              <SelectTrigger className="h-9 w-[140px] bg-background">
                                <SelectValue>
                                  {permissionValue && (
                                    <div className="flex items-center gap-1 pr-1">
                                      <div className={cn(
                                        "p-0.5 rounded-md",
                                        permissionValue === "EDITOR" && "bg-blue-500/10",
                                        permissionValue === "VIEWER" && "bg-gray-500/10",
                                        permissionValue === "RESTRICTED" && "bg-destructive/10"
                                      )}>
                                        {(() => {
                                          const PermissionIcon = PERMISSION_LEVELS[permissionValue].icon;
                                          return (
                                            <PermissionIcon className={cn(
                                              "h-3.5 w-3.5",
                                              PERMISSION_LEVELS[permissionValue].color
                                            )} />
                                          );
                                        })()}
                                      </div>
                                      <span className={cn(
                                        "text-sm font-medium truncate",
                                        PERMISSION_LEVELS[permissionValue].color
                                      )}>
                                        {PERMISSION_LEVELS[permissionValue].label}
                                      </span>
                                    </div>
                                  )}
                                </SelectValue>
                              </SelectTrigger>
                              <SelectContent>
                                {availablePermissions.map((permission) => {
                                  const { label, description, icon: Icon, color } = PERMISSION_LEVELS[permission];
                                  return (
                                    <SelectItem
                                      key={permission}
                                      value={permission}
                                      className="flex items-center gap-2"
                                    >
                                      <div className="flex items-center gap-3">
                                        <div className={cn(
                                          "p-1 rounded-md",
                                          permission === "EDITOR" && "bg-blue-500/10",
                                          permission === "VIEWER" && "bg-gray-500/10",
                                          permission === "RESTRICTED" && "bg-destructive/10"
                                        )}>
                                          <Icon className={cn(
                                            "h-3.5 w-3.5",
                                            color
                                          )} />
                                        </div>
                                        <div className="flex flex-col">
                                          <span className="text-sm font-medium">{label}</span>
                                          <span className="text-xs text-muted-foreground">{description}</span>
                                        </div>
                                      </div>
                                    </SelectItem>
                                  );
                                })}
                              </SelectContent>
                            </Select>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </ScrollArea>
              </CardContent>
            </Card>
          </div>
        </div>

        <DialogFooter className="border-t bg-muted/20 px-6 py-3 flex flex-col-reverse sm:flex-row gap-2 shrink-0">
          <Button 
            variant="outline" 
            onClick={() => onOpenChange(false)}
            size="sm"
            className="w-full sm:w-auto"
          >
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            size="sm"
            className="w-full sm:w-auto gap-2"
          >
            {isSaving ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              "Save Changes"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
